import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, BadgeCheck, Search, Star } from 'lucide-react';
import api, { fileUrl } from './api';

const Avatar = ({ op, size = 56 }) => (
  <div className="shrink-0 overflow-hidden rounded-full bg-[#e9f3ec] ring-2 ring-[#3a9b5c]/25" style={{ width: size, height: size }}>
    {op.photoFileId
      ? <img src={fileUrl(op.photoFileId)} alt={op.name} className="h-full w-full object-cover" />
      : <div className="flex h-full w-full items-center justify-center font-bold text-[#3a9b5c]">{(op.name || '?').charAt(0)}</div>}
  </div>
);

const Rating = ({ value, count }) => (
  <span className="flex items-center gap-1 text-xs text-gray-500" data-testid="pro-rating">
    <Star size={13} className={value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
    {value ? `${value.toFixed(1)} (${count})` : 'New'}
  </span>
);

const FindProfessional = () => {
  const [ops, setOps] = useState([]);
  const [cats, setCats] = useState([]);
  const [boroughs, setBoroughs] = useState([]);
  const [category, setCategory] = useState('');
  const [borough, setBorough] = useState('');
  const [minRating, setMinRating] = useState('');
  const [loading, setLoading] = useState(true);

  const search = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (category) p.append('category', category);
    if (borough) p.append('area', borough);
    if (minRating) p.append('minRating', minRating);
    api.get(`/operatives?${p.toString()}`).then((r) => setOps(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/marketplace/config').then((r) => { setCats(r.data.categories); setBoroughs(r.data.boroughs || []); });
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="find-professional-page">
      <section className="ea-hero-gradient py-14"><div className="mx-auto max-w-6xl px-5">
        <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Find a Verified Professional</h1>
        <p className="mt-2 max-w-2xl text-white/85">Browse vetted London tradespeople — every professional is verified by our team before they appear here.</p>
      </div></section>

      <section className="bg-[#e9f3ec] py-10"><div className="mx-auto max-w-6xl px-5">
        <div className="mb-7 rounded-2xl bg-white p-4 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Service Category</label>
              <select data-testid="pro-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"><option value="">All Trades</option>{cats.map((c) => <option key={c}>{c}</option>)}</select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">London Borough / Area</label>
              <select data-testid="pro-area-filter" value={borough} onChange={(e) => setBorough(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"><option value="">All Boroughs</option>{boroughs.map((b) => <option key={b}>{b}</option>)}</select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Minimum Rating</label>
              <select data-testid="pro-rating-filter" value={minRating} onChange={(e) => setMinRating(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"><option value="">Any Rating</option><option value="4">4★ &amp; up</option><option value="4.5">4.5★ &amp; up</option><option value="5">5★ only</option></select>
            </div>
            <div className="flex items-end gap-3">
            <div className="flex items-center gap-2 rounded-full bg-[#e9f3ec] px-3 py-2 text-xs font-medium text-[#3a9b5c]" title="Every professional listed is verified by our team">
              <BadgeCheck size={15} /> All professionals verified
            </div>
            <button data-testid="pro-search-btn" onClick={search} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#3a9b5c] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#318150]"><Search size={16} /> Search</button>
            </div>
          </div>
        </div>

        {loading ? <p className="py-10 text-center text-gray-500">Loading professionals…</p> : ops.length === 0 ? (
          <p className="rounded-2xl bg-white p-10 text-center text-gray-500 shadow">No verified professionals match your search yet. Try widening your filters.</p>
        ) : (
          <>
            <p className="mb-4 text-sm text-gray-500">{ops.length} verified professional{ops.length === 1 ? '' : 's'} found</p>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="professionals-grid">
              {ops.map((op) => (
                <Link to={`/professionals/${op.id}`} key={op.id} data-testid={`pro-card-${op.id}`} className="ea-card-hover flex min-w-0 flex-col overflow-hidden rounded-2xl bg-white p-6 shadow-sm">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar op={op} />
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 truncate font-display text-lg font-bold text-[#1b4f72]">{op.name} <BadgeCheck size={16} className="shrink-0 text-[#3a9b5c]" /></p>
                      {op.companyName && <p className="truncate text-xs text-gray-400">{op.companyName}</p>}
                      <Rating value={op.rating} count={op.reviewCount} />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">{op.categories.slice(0, 3).map((c) => <span key={c} className="rounded-full bg-[#e9f3ec] px-2.5 py-0.5 text-xs font-medium text-[#3a9b5c]">{c}</span>)}</div>
                  {op.bio && <p className="mt-3 line-clamp-2 break-words text-sm text-gray-600">{op.bio}</p>}
                  {op.serviceAreas.length > 0 && <p className="mt-3 flex items-center gap-1.5 break-words text-xs text-gray-500"><MapPin size={13} className="shrink-0" /> {op.serviceAreas.slice(0, 3).join(', ')}</p>}
                  {op.hourlyRate != null && <p className="mt-1 text-sm font-semibold text-[#1b4f72]">From £{op.hourlyRate}/hr</p>}
                </Link>
              ))}
            </div>
          </>
        )}
      </div></section>
    </div>
  );
};

export default FindProfessional;
